import { useQuery, useZero } from "@rocicorp/zero/react";
import { nanoid } from "nanoid";
import { useEffect, useMemo, useRef, useState } from "react";
import { getInitialConnectorValues } from "../../../shared/connectors/getInitialConnectorValues";
import type { AppTag } from "../../../shared/connectors/tags";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import { config } from "../../environment";
import { useDefaultConnectorGrants } from "../../hooks/useDefaultConnectorGrants";
import { useHasAccess } from "../../hooks/useHasAccess";
import { connectorDirectory } from "../../lib/connectorDirectory";
import {
  useMakeUrl,
  navigate,
  Route,
  routes,
  useCurrentUrl,
} from "../../lib/urls";
import { ConnectorOption } from "../ConnectorOption";
import { useRenderWithPlaceholders } from "../ConnectorModal/renderWithPlaceholders";
import { useModalId } from "../globalModalStack";
import { MainScrollContainer } from "../MainScrollContainer";
import { Directory } from "./Directory";

export function DirectoryPage({ active = true }: { active?: boolean }) {
  const makeUrl = useMakeUrl();
  const z = useZero();
  const renderWithPlaceholders = useRenderWithPlaceholders();
  const canCreateApp = useHasAccess("tenant:connected_app_create");
  const grants = useDefaultConnectorGrants();
  const { q: searchQuery, tag: selectedTag } = useCurrentUrl(
    routes.connectors.library
  );
  const [createdAppId, setCreatedAppId] = useState<string | null>(null);

  const visibleApps = useMemo(() => {
    let result = connectorDirectory;
    if (selectedTag !== "all") {
      result = result.filter((app) => app.tags.includes(selectedTag as AppTag));
    }
    if (searchQuery?.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (app) =>
          app.name.toLowerCase().includes(query) ||
          app.definition.keywords?.toLowerCase().includes(query)
      );
    }
    return result;
  }, [searchQuery, selectedTag]);

  // Card links deliberately freeze the URL state they were computed against:
  // recomputing them when modal params enter the URL would re-render every
  // card for links that can't be clicked while a modal covers the page.
  const createLinks = useMemo(
    () =>
      new Map(
        visibleApps.map((app) => [
          app.identifier,
          makeUrl(routes.connectors.library.create, {
            identifier: app.identifier,
          }),
        ])
      ),
    [visibleApps, makeUrl]
  );

  const customApp = connectorDirectory.find(
    (app) => app.identifier === "apiKey"
  );

  const [connector] = useQuery(
    queries.loadConnectorDetails({ connectedAppId: createdAppId ?? "" }),
    { enabled: !!createdAppId }
  );

  const connectorModalOpen = useModalId("connector");

  useEffect(() => {
    if (createdAppId && !connectorModalOpen) {
      const justCreated = connector && !connector.fresh ? connector.id : "";
      navigate(routes.connectors.library, { justCreated }, { replace: true });
      // oxlint-disable-next-line react/react-compiler -- EffectSetState: clears the created-app id after navigating once the connector modal closes
      setCreatedAppId(null);
    }
  }, [createdAppId, connectorModalOpen, connector]);

  const pendingHandled = useRef(false);

  const captureCreate = ({ identifier }: { identifier?: string }) => {
    if (pendingHandled.current) return null;
    if (!canCreateApp) return null;
    pendingHandled.current = true;

    const appDef = connectorDirectory.find((a) => a.identifier === identifier);
    if (!appDef) return null;

    const newAppId = nanoid(12);
    const initialValues = getInitialConnectorValues(
      appDef.definition,
      renderWithPlaceholders
    );

    z.mutate(
      sharedMutators.createConnectedApp({
        connectedAppId: newAppId,
        name: initialValues.name,
        identifier: appDef.identifier,
        urlPattern: initialValues.urlPattern,
        configuration: initialValues.configuration,
        secrets: {},
        createOAuthConnection: initialValues.createOAuthConnection,
        grants,
      })
    );

    pendingHandled.current = false;
    setCreatedAppId(newAppId);
    navigate(routes.modal.connector, { id: newAppId });
    navigate(routes.connectors.library, {}, { replace: true });
    return null;
  };

  return (
    <>
      {active && <title>{`Library • ${config.title}`}</title>}
      <MainScrollContainer>
        <Route path={routes.connectors.library.create}>{captureCreate}</Route>

        <Directory>
          {visibleApps.map((app) => (
            <ConnectorOption
              key={app.identifier}
              app={app}
              to={createLinks.get(app.identifier)!}
              disabled={!canCreateApp}
            />
          ))}

          {visibleApps.length === 0 && customApp && (
            <ConnectorOption
              app={customApp}
              to={makeUrl(routes.connectors.library.create, {
                identifier: customApp.identifier,
              })}
              disabled={!canCreateApp}
            />
          )}
        </Directory>
      </MainScrollContainer>
    </>
  );
}
